import { Button, Flex, Menu, MenuItem, MenuTrigger, TagBadge, Text } from '@chatsdet/ui';
import { DotsThreeVertical, Trash } from '@phosphor-icons/react';
import type { WorkflowRunSummaryDto } from '../hooks/useWorkflowRuns';
import { formatDuration, formatRelativeTime, runStatusLabel } from './run-formatters';
import { formatUpdatedAt } from '../utils';

const statusDotClass: Record<WorkflowRunSummaryDto['status'], string> = {
  queued: 'bg-muted-foreground/60',
  running: 'bg-warning',
  success: 'bg-success',
  failed: 'bg-destructive',
  inconclusive: 'bg-muted-foreground/60',
  canceled: 'bg-muted-foreground/60',
  timeout: 'bg-muted-foreground/60',
};

export function RunHistoryCard({
  run,
  isSelected,
  onSelect,
  onDelete,
}: {
  run: WorkflowRunSummaryDto;
  isSelected: boolean;
  onSelect: (runId: string) => void;
  onDelete: (runId: string) => void;
}) {
  const durationMs =
    run.startedAt && run.finishedAt
      ? new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()
      : null;

  const primaryTimestamp = run.finishedAt ?? run.startedAt ?? run.createdAt;

  // `skipped` counts as done only on a successful run, because it means two
  // different things: on a success a branch or condition declined the step, so
  // nothing is outstanding; on any other outcome execution never reached it.
  // `warned` steps did execute — warn-severity assertions that do not fail the
  // run.
  const stepDone =
    run.stepTotals.success +
    run.stepTotals.failed +
    run.stepTotals.warned +
    (run.status === 'success' ? run.stepTotals.skipped : 0);

  const stepQualifiers: string[] = [];
  if (run.stepTotals.failed > 0) {
    stepQualifiers.push(`${run.stepTotals.failed} failed`);
  }
  if (run.stepTotals.warned > 0) {
    stepQualifiers.push(`${run.stepTotals.warned} warning${run.stepTotals.warned > 1 ? 's' : ''}`);
  }

  // Build version label
  const versionLabel = run.workflowRevision
    ? `Revision ${run.workflowRevision.number}`
    : run.snapshotVersion
      ? `v${run.snapshotVersion}`
      : run.draftSnapshot
        ? run.draftSnapshot.basePublishedVersion > 0
          ? `v${run.draftSnapshot.basePublishedVersion}.${run.draftSnapshot.version}`
          : `v0.${run.draftSnapshot.version}`
        : null;

  return (
    <div
      role="button"
      tabIndex={0}
      className={[
        'w-full text-left px-4 py-2.5 transition-colors cursor-pointer',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        isSelected ? 'bg-muted/50' : 'hover:bg-muted/30',
      ].join(' ')}
      onClick={() => onSelect(run.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(run.id);
        }
      }}
    >
      <Flex justify="between" align="center" gap="sm">
        {/* Left: Status dot + condensed info */}
        <Flex align="center" gap="sm" className="min-w-0 flex-1">
          <span
            className={[
              'h-2 w-2 shrink-0 rounded-full',
              statusDotClass[run.status],
              run.status === 'running' ? 'animate-pulse' : '',
            ].join(' ')}
            title={runStatusLabel(run.status)}
          />
          <Text size="sm" className="shrink-0">
            {runStatusLabel(run.status)}
          </Text>
          <Text size="xs" className="text-muted-foreground shrink-0">
            {formatDuration(durationMs)}
          </Text>
          <Text size="xs" className="text-muted-foreground truncate">
            {run.executionMode === 'api' && !(run.apiCalls === null && run.stepTotals.total > 0) ? (
              run.apiCalls ? (
                <>
                  {run.apiCalls.success}/{run.apiCalls.total} calls
                  {run.status === 'failed' ? ' (failed)' : ''}
                </>
              ) : (
                // No call data and no steps either — nothing to count, so name
                // the mode. Runs that are api-mode only because they never
                // touch a browser do have steps and take the step branch below.
                'API sequence'
              )
            ) : (
              <>
                {stepDone}/{run.stepTotals.total} steps
                {stepQualifiers.length > 0 ? ` (${stepQualifiers.join(', ')})` : ''}
              </>
            )}
          </Text>
          {run.scenarioName && (
            <TagBadge
              name={run.scenarioName}
              color="purple"
              size="sm"
              hideDot
              className="shrink-0"
            />
          )}
        </Flex>

        {/* Right: Timestamp + menu */}
        <Flex align="center" gap="xs" className="shrink-0">
          <Text
            size="xs"
            className="text-muted-foreground"
            title={formatUpdatedAt(primaryTimestamp)}
          >
            {formatRelativeTime(primaryTimestamp)}
          </Text>
          <div onClick={(e) => e.stopPropagation()}>
            <MenuTrigger>
              <Button
                variant="ghost"
                size="sm"
                className="p-1 text-muted-foreground opacity-0 group-hover:opacity-100 focus:opacity-100"
                aria-label="Run actions"
              >
                <DotsThreeVertical size={16} weight="bold" />
              </Button>
              <Menu
                onAction={(key) => {
                  if (key === 'delete') onDelete(run.id);
                }}
              >
                <MenuItem id="delete" destructive>
                  <Trash size={14} className="mr-2" />
                  Delete
                </MenuItem>
              </Menu>
            </MenuTrigger>
          </div>
        </Flex>
      </Flex>

      {/* Version label (scenario name moved to its own chip in the header row) */}
      {versionLabel && (
        <Text size="xs" className="text-muted-foreground mt-1 ml-4 font-mono">
          {versionLabel}
        </Text>
      )}

      {/* Resume indicator - simplified */}
      {run.resumeAt && (
        <Text size="xs" className="text-muted-foreground mt-1 ml-4">
          Waiting until {formatUpdatedAt(run.resumeAt)}
        </Text>
      )}
    </div>
  );
}
